// Resolving next / prev against the rows the inbox list actually shows, so
// "e", j/k and the chevrons always walk the visible order whatever the sort is.

export interface ListRowWindow {
	rowAt: (index: number) => { id: string } | undefined
	length: number
}

export interface ListNeighbors {
	index: number | null
	prevId?: string
	nextId?: string
}

export interface PinnedListNeighbors {
	threadId: string
	prevId?: string
	nextId?: string
}

// Neighbours are captured once per open thread. Answering a customer rewrites
// the thread's sort key, so re-resolving would hand back the rows around
// wherever the list just moved it instead of the queue position the reader had.
export function keepPinnedNeighbors(
	pinned: PinnedListNeighbors | null,
	threadId: string,
): boolean {
	if (!pinned || pinned.threadId !== threadId) return false
	return Boolean(pinned.prevId ?? pinned.nextId)
}

// A pinned neighbour that left the list (done, snoozed, reassigned) would open
// outside this tab and drag the inbox to its Done tab; the cursor query skips it.
export function livePinnedNeighbors(
	pinned: PinnedListNeighbors | null,
	rows: ListRowWindow,
): PinnedListNeighbors | null {
	if (!pinned) return null
	const inList = (id: string | undefined) =>
		!!id && findRowIndexById(rows, id) !== null
	return {
		threadId: pinned.threadId,
		prevId: inList(pinned.prevId) ? pinned.prevId : undefined,
		nextId: inList(pinned.nextId) ? pinned.nextId : undefined,
	}
}

// Rows outside the loaded window read as undefined, so a scan stays cheap. The
// cap keeps a huge estimated total from turning a keypress into a long loop.
const MAX_SCAN = 2000

export function findRowIndexById(
	{ rowAt, length }: ListRowWindow,
	threadId: string | null | undefined,
	hintIndex?: number | null,
	maxScan: number = MAX_SCAN,
): number | null {
	if (!threadId) return null
	if (
		hintIndex !== null &&
		hintIndex !== undefined &&
		hintIndex >= 0 &&
		rowAt(hintIndex)?.id === threadId
	) {
		return hintIndex
	}
	const limit = Math.min(length, maxScan)
	for (let i = 0; i < limit; i++) {
		if (rowAt(i)?.id === threadId) return i
	}
	return null
}

// Row 0 is only the entry point for a list nobody is reading yet. With a thread
// open and its row unresolved, staying put beats teleporting to the top.
export function moveTargetIndex({
	index,
	length,
	direction,
	hasOpenThread,
}: {
	index: number | null
	length: number
	direction: "down" | "up"
	hasOpenThread: boolean
}): number | null {
	if (index === null) {
		if (hasOpenThread || length === 0) return null
		return 0
	}
	if (direction === "down") return index < length - 1 ? index + 1 : null
	return index > 0 ? index - 1 : null
}

// An unresolved neighbour stays undefined on purpose: the caller then falls
// back to its cursor query instead of guessing a row.
export function resolveListNeighbors(
	rows: ListRowWindow,
	threadId: string | null | undefined,
	hintIndex?: number | null,
	maxScan: number = MAX_SCAN,
): ListNeighbors {
	const index = findRowIndexById(rows, threadId, hintIndex, maxScan)
	if (index === null) return { index: null }
	return {
		index,
		prevId: index > 0 ? rows.rowAt(index - 1)?.id : undefined,
		nextId: rows.rowAt(index + 1)?.id,
	}
}
